import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, Flex, Grid, GridDragArea, Icon, theme } from "uikit";
import { useDebouncedCallback } from "use-debounce/lib";
import { SortType } from "rsuite-table";
import { isEqual } from "lodash";
import { useDispatch } from "react-redux";

import Parking, { ParkingSubscribeType } from "../../../services/Parking2";
import { useTypedSelector } from "../../../redux/store";
import { ordersAction as orders } from "../../../redux/reducers/Orders";
import {
	useTaxiServiceFilterAccessControl,
	useParkingSubHandler,
} from "../../../hooks";
import { AccessGuard, StyledRow, StyledColumn } from "../../common";
import { SVGgeneral } from "../../../utils/generalSprite";
import DeleteModal from "../../DeleteModal";
import SearchBar from "../../SearchBar";
import { useTableOptions } from "../../LightTable";
import { useFilteredExecutors } from "../hook";

import { TabAccessNames, TabKeys } from "./constants/access";
import useParkingActions from "./hooks/useParkingActions";
import { Filter, ParkingsModal, Table } from "./components";

const Header = styled(StyledRow)`
	height: 40px;
	width: 100%;
	background: #ffffff;
	z-index: 9;
	padding: 4px 12px;
	box-shadow: 0px 2px 4px #4b576126;
`;

const DragButtonRoot = styled(Flex)`
	flex-basis: 18px;

	cursor: grab;
`;

const StyledSearchBar = styled(SearchBar)`
	width: 280px;
`;

function DragButton() {
	const item = useContext(Grid.Item.ItemContext);

	return item?.indexes.length === 1 ? (
		<GridDragArea.GridDragArea indexes={item.indexes}>
			<DragButtonRoot align="center" justify="start">
				<SVGgeneral id="drag_icon" />
			</DragButtonRoot>
		</GridDragArea.GridDragArea>
	) : null;
}

function ParkingsWidget({ taxiServiceData }) {
	const dispatch = useDispatch();
	const { onChangeParkingsPosition } = useParkingActions();
	const { getOrderAndLimitParkingSubHandler } = useParkingSubHandler();
	const { subTaxiServiceFilterAccess } =
		useTaxiServiceFilterAccessControl<Parking.Model>();
	const { t } = useTranslation();

	const allParkingsData = useTypedSelector(
		(state) => state.globalState.parking.data.subscription.basic,
	);

	const parkingsSubscribeData = useMemo(() => {
		if (!Array.isArray(allParkingsData)) {
			return {
				models: [],
				metadataState: { total: 0 },
			};
		}

		const dataAfterAccess = subTaxiServiceFilterAccess({
			models: allParkingsData,
			branchPath: "taxiServiceId",
		});

		return {
			models: dataAfterAccess,
			metadataState: { total: dataAfterAccess.length },
		};
	}, [allParkingsData, subTaxiServiceFilterAccess]);

	const lang = useTypedSelector((state) => state.session.language);
	const data = useTypedSelector((state) => state.orders.parking.data);
	const { criteria: filters } = useTypedSelector(
		(state) => state.orders.parking.filter,
	);

	const [showParkingModal, setShowParkingModal] = useState<boolean>(false);
	const [booferQuery, setBooferQuery] = useState(filters.query || "");
	const [isEditModal, setIsEditModal] = useState<boolean>(false);
	const [showClearParkings, setShowClearParkings] = useState(false);
	const [addressSearchFieldQuery, setAddressSearchFieldQuery] = useState({
		settlement: "",
		address: "",
		street: "",
	});
	const [addressSearchFieldError, setAddressSearchFieldError] =
		useState<boolean>(false);
	const [selectedParkingsTableId, setSelectedParkingsTableId] =
		useState<number>(0);
	const [
		searchedCompanyAndTaxiServiceIds,
		setSearchedCompanyAndTaxiServiceIds,
	] = useState<{ companyId: number; taxiServiceId: number } | null>(null);

	const onChangeParkingsQuery = useCallback(() => {
		dispatch(orders.parking.filter.setParkingsQuery(booferQuery));
	}, [booferQuery, dispatch]);

	const debounceExecutors = useDebouncedCallback(onChangeParkingsQuery, 200);

	const onSubmitAddress = useCallback(
		async (coordinatesData) => {
			try {
				const response = await Parking.getAll({
					point: coordinatesData.coordinates,
					subscribeType: ParkingSubscribeType.MAIN_ORDER_GET_ALL_PAGE,
				});

				if (response.items.length > 0) {
					const parking = response.items[0];
					if (
						filters.taxiServiceIds.includes(parking.taxiService.id)
					) {
						setSelectedParkingsTableId(parking.id);
						setSearchedCompanyAndTaxiServiceIds({
							companyId: parking.taxiService.company.id,
							taxiServiceId: parking.taxiService.id,
						});
						setAddressSearchFieldError(false);
						setIsEditModal(true);
					} else {
						setAddressSearchFieldError(true);
					}
				} else {
					setAddressSearchFieldError(true);
				}
				setAddressSearchFieldQuery({
					settlement: `${
						coordinatesData?.street?.data?.settlement || ""
					}`,
					address: `${
						coordinatesData?.street?.data?.streetType || ""
					} ${coordinatesData?.street?.data?.street || ""}`,
					street: `${coordinatesData?.house?.data?.number || ""}`,
				});
			} catch (error) {
				console.error(error);
			}
		},
		[filters.taxiServiceIds],
	);

	const parkings = useMemo<Parking.Model[]>(
		() => parkingsSubscribeData?.models ?? [],
		[parkingsSubscribeData?.models],
	);

	const dataForTable = useMemo(() => {
		const dataArray: {
			type?: SortType;
			column?: SortType;
			data: Parking.Model[];
		}[] = [];

		const taxiServiceIds: number[] = [...filters.taxiServiceIdsPosition];
		parkings.forEach((parking) => {
			if (!taxiServiceIds.includes(parking.taxiServiceId)) {
				taxiServiceIds.push(parking.taxiServiceId);
			}
		});
		taxiServiceIds.forEach((taxiServiceId) => {
			const parkingsByTaxiServiceId = parkings.filter(
				(parking) => parking.taxiServiceId === taxiServiceId,
			);
			dataArray.push({
				type: undefined,
				column: undefined,
				data: parkingsByTaxiServiceId,
			});
		});
		const filtredDataArray = dataArray.filter(
			(parking) => parking.data.length,
		);

		const retval = {
			data: filtredDataArray,
			taxiServiceIds,
		};

		return retval;
	}, [filters, parkings]);

	const allReservedIds = useMemo(() => {
		const result: string[] = [];

		if (!parkings) return result;

		for (const parking of parkings) {
			const queue = parking.executorsQueue;
			if (queue) {
				for (const item of queue) {
					const callSign = item?.executor?.callSign;
					if (callSign) {
						result.push(callSign);
					}
				}
			}
		}

		return result;
	}, [parkings]);

	const filteredExecutors = useFilteredExecutors(
		data.executors,
		{
			companyIds: filters.companyIds,
			taxiServiceIds: filters.taxiServiceIds,
			executorGroupIds: filters.executorGroupIds,
		},
		{
			companyIds: true,
			taxiServiceIds: true,
			executorGroupIds: true,
		},
	);

	const calculateExecutors = useMemo(() => {
		const filtredExecutors = filteredExecutors.filter((executor) => {
			const statusMatch =
				!filters.statuses.length ||
				filters.statuses.includes(executor.status);

			const taxiServiceMatch =
				!filters.taxiServiceIds.length ||
				filters.taxiServiceIds.includes(executor?.taxiService?.id || 0);

			const onlineStatus = executor.online ? "online" : "offline";

			const onlineMatch =
				!filters.online.length || filters.online.includes(onlineStatus);

			return statusMatch && taxiServiceMatch && onlineMatch;
		});

		const onParking = filtredExecutors.filter(
			(executor) => executor?.currentParking,
		);
		const outsideParking = filtredExecutors.filter(
			(executor) => executor?.currentParking == null,
		);

		const executorOnline = filtredExecutors.reduce(
			(acc, executor) => acc + +executor.online,
			0,
		);

		const executorsOffline = filtredExecutors.reduce(
			(acc, executor) => acc + +!executor.online,
			0,
		);

		return {
			onParking: onParking.length,
			outsideParking: outsideParking.length,
			online: executorOnline,
			offline: executorsOffline,
		};
	}, [
		filteredExecutors,
		filters.online,
		filters.statuses,
		filters.taxiServiceIds,
	]);

	const parkingsModalData = useMemo(
		() =>
			parkings.find((parking) => parking.id === selectedParkingsTableId),
		[parkings, selectedParkingsTableId],
	);

	const openClearParkingsModal = useCallback(() => {
		setShowClearParkings(true);
	}, []);
	const closelearParkingsModal = useCallback(() => {
		setShowClearParkings(false);
	}, []);

	const tableFooter = useMemo(
		() => (
			<>
				<StyledRow
					gap="20px"
					style={{
						borderTop: `1.5px solid ${theme.colors.button_secondary}`,
						paddingInline: "8px",
						paddingBlock: "4px",
						backgroundColor: "#EBEBEB",
						fontSize: "12px",
						fontWeight: "600",
						color: "#21333F",
					}}
				>
					<span>
						{t("orderPageWidgets.parking.str200") ?? ""}
						{":  "}
						{calculateExecutors.onParking}
					</span>
					<span>
						{t("orderPageWidgets.parking.str201") ?? ""}
						{": "}
						{calculateExecutors.outsideParking}
					</span>
					<span>
						{t("orderPageWidgets.parking.str202") ?? ""}:{" "}
						{calculateExecutors.online}
					</span>
					<span>
						{t("orderPageWidgets.parking.str203") ?? ""}:{" "}
						{calculateExecutors.offline}
					</span>
				</StyledRow>
			</>
		),
		[
			calculateExecutors.onParking,
			calculateExecutors.outsideParking,
			calculateExecutors.online,
			calculateExecutors.offline,
			t,
		],
	);

	const onSaveData = useCallback(
		async (parkingId: number, executorIds: number[], isDataWasChanged) => {
			try {
				if (!isDataWasChanged) return;
				const executorsOnParkingIds = data?.executors
					?.filter((executor) => !!executor.currentParking)
					.map((executor) => executor?.id);

				const currentParking = allParkingsData?.find(
					(parking) => parking?.id === parkingId,
				);
				const executorIdsFromParking =
					currentParking?.executorsQueue?.map(
						(queue) => queue?.executor?.id,
					);

				const executorIdsToChange = executorIds?.filter(
					(executorId) =>
						!executorIdsFromParking?.includes(executorId) &&
						executorsOnParkingIds.includes(executorId),
				);

				const executorIdsDataToResponse = executorIdsToChange.map(
					(executorId) => ({ executorId }),
				);

				await Parking.change(parkingId, executorIdsDataToResponse);

				await Parking.update({
					id: parkingId,
					executorsQueue: executorIds,
				});
			} catch (error) {
				console.error(error);
			} finally {
				setShowParkingModal(false);
				setIsEditModal(false);
				setAddressSearchFieldQuery({
					address: "",
					settlement: "",
					street: "",
				});
				setAddressSearchFieldError(false);
				setSearchedCompanyAndTaxiServiceIds(null);
			}
		},
		[allParkingsData, data.executors],
	);

	const closeParkingModal = useCallback(() => {
		setShowParkingModal(false);
		setIsEditModal(false);
		setAddressSearchFieldQuery({
			address: "",
			settlement: "",
			street: "",
		});
		setSearchedCompanyAndTaxiServiceIds(null);
		setAddressSearchFieldError(false);
	}, []);

	const editParking = useCallback(() => {
		setIsEditModal(true);
		setShowParkingModal(true);
	}, []);

	const clearSearchField = useCallback(() => {
		setBooferQuery("");
	}, []);

	const allParkingsIdsWithExecutors = useMemo(() => {
		const allParkingsWithExecutors =
			parkingsSubscribeData?.models?.filter(
				(parking) => parking.executorsQueue?.length,
			) ?? [];
		const parkingsIdsToClear = allParkingsWithExecutors.map(
			(parking) => parking.id,
		);
		return parkingsIdsToClear;
	}, [parkingsSubscribeData]);

	const removeAllExecutorsFromAllParkings = useCallback(() => {
		allParkingsIdsWithExecutors.map(async (parkingId) => {
			await Parking.update({
				id: parkingId,
				executorsQueue: [],
			});
		});
		closelearParkingsModal();
	}, [allParkingsIdsWithExecutors, closelearParkingsModal]);

	useEffect(() => {
		const models = parkingsSubscribeData?.models ?? [];
		if (!models.length) return;

		const parkingTaxiServiceIds = new Set(
			models.map((model) => model.taxiServiceId),
		);
		const nextPosition = filters.taxiServiceIdsPosition.filter((id) => {
			if (!parkingTaxiServiceIds.has(id)) return false;
			if (!filters.taxiServiceIds?.length) return true;
			return filters.taxiServiceIds.includes(id);
		});

		if (!isEqual(nextPosition, filters.taxiServiceIdsPosition)) {
			onChangeParkingsPosition(nextPosition);
		}
	}, [
		filters.taxiServiceIds,
		filters.taxiServiceIdsPosition,
		onChangeParkingsPosition,
		parkingsSubscribeData?.models,
	]);

	useEffect(() => {
		debounceExecutors();
	}, [booferQuery]);

	const { sort, editor, onChange } = useTableOptions();

	const nameColuumnsGroupFirst: ParkingsWidget.TabMain = useMemo(
		() => ({
			reduxLocal: "OrdersPage2.orderWidgetParkings",
			name: "orderWidgetParkings",
		}),
		[],
	);

	const contentOnEdit = useCallback(() => {}, []);

	const parkingSubWithSort = useMemo(() => {
		const options: Parking.SubscribeOptions = {};

		if (sort.dataKey && sort.sortType)
			options.order = {
				[sort.dataKey as keyof Parking.SubscribeOptions["order"]]:
					sort.sortType,
			};

		return getOrderAndLimitParkingSubHandler(
			options,
			parkingsSubscribeData?.models ?? [],
		);
	}, [
		sort.dataKey,
		sort.sortType,
		getOrderAndLimitParkingSubHandler,
		parkingsSubscribeData?.models,
	]);

	return (
		<StyledColumn w="100%" h="100%">
			<Header justify="space-between" gap="4px">
				<StyledRow gap="4px" alignItems="center">
					<DragButton />
					<AccessGuard
						accessName={TabAccessNames[TabKeys.CONTROL_BUTTON_ADD]}
					>
						<Button.Button
							icon={<Icon id="plus" size={16} />}
							onClick={() => setShowParkingModal(true)}
						/>
					</AccessGuard>
					<AccessGuard
						accessName={TabAccessNames[TabKeys.CONTROL_BUTTON_EDIT]}
					>
						<Button.Button
							variant="secondary"
							onClick={editParking}
							icon={<Icon id="pencil" size={20} />}
							disabled={!selectedParkingsTableId}
						/>
					</AccessGuard>
					<AccessGuard
						accessName={
							TabAccessNames[TabKeys.CONTROL_BUTTON_DELETE]
						}
					>
						<Button.Button
							icon={<Icon id="trash" size={20} />}
							variant="secondary"
							disabled={!allParkingsIdsWithExecutors.length}
							onClick={openClearParkingsModal}
						/>
					</AccessGuard>
				</StyledRow>
				<StyledRow gap="10px">
					<AccessGuard accessName={TabAccessNames[TabKeys.SEARCH]}>
						<StyledRow style={{ position: "relative" }}>
							<StyledSearchBar
								value={booferQuery}
								onChange={setBooferQuery}
								placeholder={
									t("orderPageWidgets.parking.str204") ?? ""
								}
							/>
							<div
								onClick={clearSearchField}
								style={{
									position: "absolute",
									right: 0,
									top: 9,
									cursor: "pointer",
									width: 25,
								}}
							>
								<Icon
									size={15}
									id="close"
									style={{ fill: theme.colors.disabled_text }}
								/>
							</div>
						</StyledRow>
					</AccessGuard>
					<AccessGuard accessName={TabAccessNames[TabKeys.FILTER]}>
						<Filter
							taxiServiceData={taxiServiceData}
							taxiServiceIdsPosition={dataForTable.taxiServiceIds}
							language={lang}
						/>
					</AccessGuard>
				</StyledRow>
			</Header>

			<Table
				onChangeIsEditModal={setIsEditModal}
				onShowParkingModal={setShowParkingModal}
				data={parkingSubWithSort}
				loading={false}
				nameColuumnsGroup={nameColuumnsGroupFirst}
				selectedId={selectedParkingsTableId}
				onChangeSelectedId={setSelectedParkingsTableId}
				onEdit={contentOnEdit}
				editorTable={editor}
				onChangeTable={onChange}
				tableFooter={
					<AccessGuard
						accessName={
							TabAccessNames[TabKeys.LOWERS_STATISTICS_TABLE]
						}
					>
						<div>{tableFooter}</div>
					</AccessGuard>
				}
			/>
			{showClearParkings && (
				<DeleteModal
					label={t("orderPageWidgets.parking.str205") ?? ""}
					onConfirm={removeAllExecutorsFromAllParkings}
					onCancel={closelearParkingsModal}
				/>
			)}
			{showParkingModal && (
				<ParkingsModal
					data={isEditModal ? parkingsModalData : (undefined as any)}
					isEditModal={isEditModal}
					addressSearchFieldQuery={addressSearchFieldQuery}
					onChangeAddressSearchFieldQuery={setAddressSearchFieldQuery}
					selectedParkingsTableId={selectedParkingsTableId}
					onChangeSelectedParkingsTableId={setSelectedParkingsTableId}
					addressSearchFieldError={addressSearchFieldError}
					onChangeAddressSearchFieldError={setAddressSearchFieldError}
					onChangeIsEditModal={setIsEditModal}
					allReservedIds={allReservedIds}
					onSave={onSaveData}
					onClose={closeParkingModal}
					language={lang}
					onSubmitAddress={onSubmitAddress}
					taxiServiceData={taxiServiceData}
					filter={filters}
					searchedCompanyAndTaxiServiceIds={
						searchedCompanyAndTaxiServiceIds
					}
				/>
			)}
		</StyledColumn>
	);
}

export declare namespace ParkingsWidget {
	interface TabMain {
		reduxLocal: "OrdersPage2.orderWidgetParkings";
		name: "orderWidgetParkings";
	}
}

export default ParkingsWidget;
